// import api from "./api"; // Asume que tienes un cliente HTTP configurado
import {
  API_MYMEDICINE_LIST,
  API_MYMEDICINE_DETAILS,
  API_MYMEDICINE_CREATE,
  API_MYMEDICINE_UPDATE,
  API_MYMEDICINE_DELETE,
  API_MYMEDICINE_HARD_DELETE,
  API_MYMEDICINE_BY_USER,
  API_MYMEDICINE_BY_PATIENT,
  API_MYMEDICINE_BY_DOCTOR,
  API_MYMEDICINE_ACTIVE,
  API_MYMEDICINE_EXPIRED,
  API_MYMEDICINE_UPCOMING,
  API_MYMEDICINE_BULK_ACTION,
  API_MYMEDICINE_EXPORT,
  API_MYMEDICINE_STATISTICS,
  API_MYMEDICINE_CALENDAR,
  API_MYMEDICINE_SCHEDULE,
  API_MYMEDICINE_REMINDERS,
  API_BASE_MYMEDICINE_URL
} from "../../../../helpers/urlHelper";

// ========================================================================
// 💊 MEDICINE SERVICE - GESTIÓN COMPLETA DE MEDICINAS
// ========================================================================

class MedicineService {
  
  // ========================================================================
  // URL BUILDER HELPER
  // ========================================================================
  
  /**
   * Construir URL completa con la base URL
   * @param {string} endpoint - Endpoint de la API
   * @returns {string} URL completa
   */
  buildUrl(endpoint) {
    return `${API_BASE_MYMEDICINE_URL}${endpoint}`;
  }

  // ========================================================================
  // OPERACIONES BÁSICAS CRUD
  // ========================================================================

  /**
   * Obtener todas las medicinas
   * @param {Object} params - Parámetros de consulta (page, limit, etc.)
   * @returns {Promise} Lista de medicinas
   */
  async getAllMedicines(params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_LIST)}?${new URLSearchParams(params)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtener medicina por ID
   * @param {string} id - ID de la medicina
   * @returns {Promise} Datos de la medicina
   */
  async getMedicineById(id) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_DETAILS)}/${id}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Crear nueva medicina
   * @param {Object} medicineData - Datos de la medicina
   * @returns {Promise} Medicina creada
   */
  async createMedicine(medicineData) {
    try {
      const response = await fetch(this.buildUrl(API_MYMEDICINE_CREATE), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(medicineData)
      });
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Actualizar medicina
   * @param {string} id - ID de la medicina
   * @param {Object} medicineData - Datos actualizados
   * @returns {Promise} Medicina actualizada
   */
  async updateMedicine(id, medicineData) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_UPDATE)}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(medicineData)
      });
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Eliminar medicina (soft delete)
   * @param {string} id - ID de la medicina
   * @returns {Promise} Confirmación de eliminación
   */
  async deleteMedicine(id) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_DELETE)}/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Eliminar medicina permanentemente
   * @param {string} id - ID de la medicina
   * @returns {Promise} Confirmación de eliminación permanente
   */
  async hardDeleteMedicine(id) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_HARD_DELETE)}/${id}/hard`, {
        method: 'DELETE'
      });
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ========================================================================
  // CONSULTAS ESPECÍFICAS
  // ========================================================================

  /**
   * Obtener medicinas por usuario
   * @param {string} userId - ID del usuario
   * @param {Object} params - Parámetros adicionales
   * @returns {Promise} Medicinas del usuario
   */
  async getMedicinesByUser(userId, params = {}) {
    try {
      const url = `${this.buildUrl(API_MYMEDICINE_BY_USER)}/${userId}?${new URLSearchParams(params)}`;
      console.log('🔍 Making request to:', url);
      
      const response = await fetch(url);
      
      // Log response details for debugging
      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', response.headers);
      
      // Check if response is ok
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response not ok:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      // Check content type
      const contentType = response.headers.get('content-type');
      console.log('📄 Content-Type:', contentType);
      
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error('❌ Response is not JSON:', responseText);
        throw new Error(`Expected JSON but received: ${contentType || 'unknown'}\nResponse: ${responseText.substring(0, 200)}...`);
      }
      
      const data = await response.json();
      console.log('✅ Parsed JSON data:', data);
      
      return data;
    } catch (error) {
      console.error('💥 Error in getMedicinesByUser:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Obtener medicinas por paciente
   * @param {string} patientId - ID del paciente
   * @param {Object} params - Parámetros adicionales
   * @returns {Promise} Medicinas del paciente
   */
  async getMedicinesByPatient(patientId, params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_BY_PATIENT)}/${patientId}?${new URLSearchParams(params)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtener medicinas prescritas por un doctor
   * @param {string} doctorId - ID del doctor
   * @param {Object} params - Parámetros adicionales
   * @returns {Promise} Medicinas prescritas por el doctor
   */
  async getMedicinesByDoctor(doctorId, params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_BY_DOCTOR)}/${doctorId}?${new URLSearchParams(params)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtener medicinas activas
   * @param {Object} params - Parámetros de consulta
   * @returns {Promise} Medicinas activas
   */
  async getActiveMedicines(params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_ACTIVE)}?${new URLSearchParams(params)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtener medicinas expiradas
   * @param {Object} params - Parámetros de consulta
   * @returns {Promise} Medicinas expiradas
   */
  async getExpiredMedicines(params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_EXPIRED)}?${new URLSearchParams(params)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtener medicinas próximas a vencer
   * @param {Object} params - Parámetros de consulta (days: número de días)
   * @returns {Promise} Medicinas próximas a vencer
   */
  async getUpcomingMedicines(params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_UPCOMING)}?${new URLSearchParams(params)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ========================================================================
  // OPERACIONES MASIVAS Y UTILIDADES
  // ========================================================================

  /**
   * Operaciones masivas (eliminar, activar, etc.)
   * @param {Object} bulkData - { action: 'delete|activate|deactivate', ids: [...] }
   * @returns {Promise} Resultado de la operación masiva
   */
  async bulkAction(bulkData) {
    try {
      const response = await fetch(this.buildUrl(API_MYMEDICINE_BULK_ACTION), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bulkData)
      });
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Exportar medicinas
   * @param {Object} params - Parámetros de exportación (format, filters)
   * @returns {Promise} Archivo de exportación
   */
  async exportMedicines(params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_EXPORT)}?${new URLSearchParams(params)}`);
      const data = await response.blob();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtener estadísticas de medicinas
   * @param {Object} params - Parámetros de las estadísticas
   * @returns {Promise} Estadísticas
   */
  async getMedicineStatistics(params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_STATISTICS)}?${new URLSearchParams(params)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtener calendario de medicinas
   * @param {Object} params - Parámetros del calendario (start, end, userId)
   * @returns {Promise} Eventos del calendario
   */
  async getMedicineCalendar(params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_CALENDAR)}?${new URLSearchParams(params)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtener horario de medicinas
   * @param {string} userId - ID del usuario
   * @param {Object} params - Parámetros adicionales
   * @returns {Promise} Horario de medicinas
   */
  async getMedicineSchedule(userId, params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_SCHEDULE)}/${userId}?${new URLSearchParams(params)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtener recordatorios de medicinas
   * @param {string} userId - ID del usuario
   * @param {Object} params - Parámetros adicionales
   * @returns {Promise} Recordatorios
   */
  async getMedicineReminders(userId, params = {}) {
    try {
      const response = await fetch(`${this.buildUrl(API_MYMEDICINE_REMINDERS)}/${userId}?${new URLSearchParams(params)}`);
      const data = await response.json();
      return data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ========================================================================
  // MÉTODOS AUXILIARES
  // ========================================================================

  /**
   * Validar datos de medicina antes del envío
   * @param {Object} medicineData - Datos a validar
   * @returns {Object} Datos validados
   */
  validateMedicineData(medicineData) {
    const required = ["assignto", "name"];
    const missing = required.filter(field => !medicineData[field]);
    
    if (missing.length > 0) {
      throw new Error(`Campos requeridos faltantes: ${missing.join(", ")}`);
    }

    // Validar fechas si existen
    if (medicineData.startdate && medicineData.enddate) {
      const start = new Date(medicineData.startdate);
      const end = new Date(medicineData.enddate);
      
      if (end < start) {
        throw new Error("La fecha de fin debe ser posterior a la fecha de inicio");
      }
    }

    return medicineData;
  }

  /**
   * Formatear datos de medicina para mostrar
   * @param {Object} medicine - Datos de la medicina
   * @returns {Object} Datos formateados
   */
  formatMedicineData(medicine) {
    return {
      ...medicine,
      formattedStartDate: medicine.startdate ? 
        new Date(medicine.startdate).toLocaleDateString() : null,
      formattedEndDate: medicine.enddate ? 
        new Date(medicine.enddate).toLocaleDateString() : null,
      isActive: medicine.status === "1",
      isExpired: medicine.enddate ? 
        new Date(medicine.enddate) < new Date() : false,
    };
  }

  /**
   * Manejar errores de las peticiones
   * @param {Error} error - Error capturado
   * @returns {Error} Error formateado
   */
  handleError(error) {
    if (error.response) {
      // Error de respuesta del servidor
      const message = error.response.data?.message || "Error del servidor";
      const status = error.response.status;
      return new Error(`${status}: ${message}`);
    } else if (error.request) {
      // Error de red
      return new Error("Error de conexión. Verifique su conexión a internet.");
    } else {
      // Error de configuración
      return new Error(error.message || "Error inesperado");
    }
  }
}

export default new MedicineService();